import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMyOrganization, getCreditsInfo, getUsageLogs, listOrganizations, getCreditRates, getStorageInfo, getTransactions, updateMyOrganization } from './api';
import type { TransactionsParams, UpdateOrganizationData } from './types';

/**
 * 組織情報取得フック
 */
export function useMyOrganization() {
  return useQuery({
    queryKey: ['organization', 'me'],
    queryFn: getMyOrganization,
    staleTime: 5 * 60 * 1000, // 5分間キャッシュ
  });
}

/**
 * クレジット情報取得フック
 */
export function useCreditsInfo() {
  return useQuery({
    queryKey: ['organization', 'credits'],
    queryFn: getCreditsInfo,
    staleTime: 1 * 60 * 1000, // 1分間キャッシュ
  });
}

/**
 * 使用量履歴取得フック
 */
export function useUsageLogs(params?: {
  start_date?: string;
  end_date?: string;
  operation_type?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['organization', 'usage', params],
    queryFn: () => getUsageLogs(params),
    staleTime: 30 * 1000, // 30秒間キャッシュ
  });
}

/**
 * 今月の使用量取得フック
 */
export function useMonthlyUsage() {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startDate = startOfMonth.toISOString().split('T')[0];

  return useUsageLogs({
    start_date: startDate,
    limit: 1000,
  });
}

// NOTE: useAddCredits は 2026-07-29 のセキュリティ監査 C-1 で削除した (呼び出し元は無かった)。

/**
 * 全組織一覧を取得するフック（プラットフォーム管理者のみ）
 */
export function useOrganizations() {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: listOrganizations,
  });
}

/**
 * クレジットレート設定を取得するフック
 */
export function useCreditRates() {
  return useQuery({
    queryKey: ['credit-rates'],
    queryFn: getCreditRates,
    staleTime: 60 * 60 * 1000, // 1時間キャッシュ（レート設定は頻繁に変更されない）
  });
}

/**
 * ストレージ使用量取得フック
 */
export function useStorageInfo() {
  return useQuery({
    queryKey: ['organization', 'storage'],
    queryFn: getStorageInfo,
    staleTime: 30 * 1000, // 30秒間キャッシュ
  });
}

/**
 * 取引履歴取得フック（ページネーション対応）
 */
export function useTransactions(params?: TransactionsParams) {
  return useQuery({
    queryKey: ['organization', 'transactions', params],
    queryFn: () => getTransactions(params),
    staleTime: 30 * 1000, // 30秒間キャッシュ
  });
}

/**
 * 組織情報更新ミューテーションフック（管理者のみ）
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateOrganizationData) => updateMyOrganization(data),
    onSuccess: () => {
      // 組織情報を再取得
      queryClient.invalidateQueries({ queryKey: ['organization', 'me'] });
    },
  });
}
